/**
 * Resolution-Based Scaling Configuration
 * 
 * This file defines the scaling system for different screen resolutions.
 * Designed for assembly line environments where operators view from 7+ meters.
 * 
 * Usage:
 * - Import scale factors in components that need resolution-aware scaling
 * - CSS variables are automatically injected by ThemeProvider
 * - Use getResolutionTier() to determine current resolution tier
 */

// ============================================
// RESOLUTION TIERS
// ============================================
export const RESOLUTION_TIERS = {
  LAPTOP: 'laptop',       // Development screens
  FHD: 'fhd',            // 1920x1080 - Standard monitors
  QHD: 'qhd',            // 2560x1440 - High-res monitors
  FOUR_K: '4k',          // 3200x1800 - 4K displays
  FOUR_K_PLUS: '4k_plus', // 3840x2160+ - Large 4K displays (assembly line)
};

// ============================================
// RESOLUTION BREAKPOINTS & SCALE FACTORS
// ============================================
/**
 * Scale factors are applied to all UI elements based on screen width
 * 
 * Example:
 * - Laptop (1366px): 16px font → 16px (1.0x)
 * - FHD (1920px): 16px font → 19.2px (1.2x)
 * - 4K+ (3840px): 16px font → 40px (2.5x) - visible from 7 meters
 */
export const RESOLUTION_BREAKPOINTS = {
  [RESOLUTION_TIERS.LAPTOP]: {
    min: 0,
    max: 1919,
    scaleFactor: 1.0,
    name: 'Laptop/Small Displays',
    description: 'Standard laptop and small monitor displays',
  },
  [RESOLUTION_TIERS.FHD]: {
    min: 1920,
    max: 2559,
    scaleFactor: 1.2,
    name: 'Full HD (1920x1080)',
    description: 'Standard desktop monitors',
  },
  [RESOLUTION_TIERS.QHD]: {
    min: 2560,
    max: 3199,
    scaleFactor: 1.5,
    name: 'QHD (2560x1440)',
    description: 'High resolution monitors',
  },
  [RESOLUTION_TIERS.FOUR_K]: {
    min: 3200,
    max: 3839,
    scaleFactor: 2.0,
    name: '4K (3200x1800)',
    description: 'Standard 4K displays',
  },
  [RESOLUTION_TIERS.FOUR_K_PLUS]: {
    min: 3840,
    max: Infinity,
    scaleFactor: 2.5,
    name: '4K+ (3840x2160+)',
    description: 'Large 4K displays for assembly line (visible from 7+ meters)',
  },
};

// ============================================
// COMPONENT-SPECIFIC SCALE MULTIPLIERS
// ============================================
/**
 * Fine-tune scaling for specific component types
 * These multipliers are applied ON TOP of the base scale factor
 * 
 * Example: Button font on 4K+ display
 * Base scale: 2.5x
 * Button multiplier: 1.1x
 * Final scale: 2.5 × 1.1 = 2.75x
 */
export const COMPONENT_SCALE_MULTIPLIERS = {
  // ========== TEXT ELEMENTS ==========
  heading: 1.0,           // H1-H6 headings
  subheading: 1.0,        // Subtitles
  body: 1.0,              // Body text
  caption: 1.0,           // Small text
  button: 1.1,            // Button text (slightly larger for readability)
  input: 1.1,             // Input fields (easier to read/interact)
  label: 1.0,             // Form labels
  
  // ========== SPACING ==========
  padding: 1.0,           // Component padding
  margin: 1.0,            // Component margins
  gap: 1.0,               // Flexbox/Grid gaps
  
  // ========== UI ELEMENTS ==========
  iconSize: 1.2,          // Icons (need to be prominent)
  imageSize: 1.0,         // Images
  borderRadius: 1.0,      // Rounded corners
  borderWidth: 0.8,       // Borders (don't scale as much, keep crisp)
  shadowSize: 1.0,        // Box shadows
  
  // ========== TABLES (Critical for assembly line) ==========
  tableCell: 1.2,         // Table cell content
  tableHeader: 1.3,       // Table headers (more prominent)
  tableRowHeight: 1.15,   // Row height
  tableFontSize: 1.25,    // Table text (crucial for visibility)
  
  // ========== FORMS ==========
  formField: 1.1,         // Form input fields
  formLabel: 1.0,         // Form labels
  checkbox: 1.2,          // Checkboxes/Radio buttons
  
  // ========== INTERACTIVE ELEMENTS ==========
  buttonHeight: 1.15,     // Button height (better touch target)
  buttonPadding: 1.1,     // Button padding
  minTouchTarget: 1.2,    // Minimum touch target size (44px → 52.8px on 4K+)
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get the current resolution tier based on screen width
 * @param {number} width - Screen width in pixels
 * @returns {string} - Resolution tier constant
 */
export const getResolutionTier = (width) => {
  if (width >= RESOLUTION_BREAKPOINTS[RESOLUTION_TIERS.FOUR_K_PLUS].min) {
    return RESOLUTION_TIERS.FOUR_K_PLUS;
  }
  if (width >= RESOLUTION_BREAKPOINTS[RESOLUTION_TIERS.FOUR_K].min) {
    return RESOLUTION_TIERS.FOUR_K;
  }
  if (width >= RESOLUTION_BREAKPOINTS[RESOLUTION_TIERS.QHD].min) {
    return RESOLUTION_TIERS.QHD;
  }
  if (width >= RESOLUTION_BREAKPOINTS[RESOLUTION_TIERS.FHD].min) {
    return RESOLUTION_TIERS.FHD;
  }
  return RESOLUTION_TIERS.LAPTOP;
};

/**
 * Get the scale factor for current screen width
 * @param {number} width - Screen width in pixels
 * @returns {number} - Scale factor (1.0 to 2.5)
 */
export const getScaleFactor = (width) => {
  const tier = getResolutionTier(width);
  return RESOLUTION_BREAKPOINTS[tier].scaleFactor;
};

/**
 * Get scale factor with component-specific multiplier applied
 * @param {number} width - Screen width in pixels
 * @param {string} componentType - Component type from COMPONENT_SCALE_MULTIPLIERS
 * @returns {number} - Final scale factor
 */
export const getComponentScale = (width, componentType = 'body') => {
  const baseScale = getScaleFactor(width);
  const multiplier = COMPONENT_SCALE_MULTIPLIERS[componentType] || 1.0;
  return baseScale * multiplier;
};

/**
 * Get resolution tier information
 * @param {number} width - Screen width in pixels
 * @returns {object} - Full tier configuration
 */
export const getResolutionInfo = (width) => {
  const tier = getResolutionTier(width);
  return {
    tier,
    ...RESOLUTION_BREAKPOINTS[tier],
  };
};

/**
 * Check if current resolution is 4K or higher
 * @param {number} width - Screen width in pixels
 * @returns {boolean}
 */
export const is4KOrHigher = (width) => {
  return width >= RESOLUTION_BREAKPOINTS[RESOLUTION_TIERS.FOUR_K].min;
};

/**
 * Check if current resolution is assembly line ready (4K+)
 * @param {number} width - Screen width in pixels
 * @returns {boolean}
 */
export const isAssemblyLineResolution = (width) => {
  return width >= RESOLUTION_BREAKPOINTS[RESOLUTION_TIERS.FOUR_K_PLUS].min;
};

// ============================================
// CSS VARIABLE NAMES
// ============================================
/**
 * CSS custom property names used throughout the application
 * These are injected by ThemeProvider and can be used in CSS/SCSS
 */
export const CSS_VARIABLES = {
  // Base scaling
  SCALE_FACTOR: '--scale-factor',
  RESOLUTION_TIER: '--resolution-tier',
  
  // Element-specific scaling
  FONT_SCALE: '--font-scale',
  SPACING_SCALE: '--spacing-scale',
  ICON_SCALE: '--icon-scale',
  BORDER_SCALE: '--border-scale',
  
  // Component-specific
  TABLE_SCALE: '--table-scale',
  BUTTON_SCALE: '--button-scale',
  INPUT_SCALE: '--input-scale',
  
  // Touch targets (important for assembly line)
  MIN_TOUCH_TARGET: '--min-touch-target',
};

// ============================================
// DEFAULT VALUES (Fallback)
// ============================================
/**
 * Default values when CSS variables are not available
 * Ensures backward compatibility
 */
export const DEFAULT_SCALE_VALUES = {
  scaleFactor: 1.0,
  fontScale: 1.0,
  spacingScale: 1.0,
  iconScale: 1.0,
  borderScale: 1.0,
  tableScale: 1.0,
  buttonScale: 1.0,
  inputScale: 1.0,
  minTouchTarget: 44, // 44px is standard minimum touch target
};

export default {
  RESOLUTION_TIERS,
  RESOLUTION_BREAKPOINTS,
  COMPONENT_SCALE_MULTIPLIERS,
  CSS_VARIABLES,
  DEFAULT_SCALE_VALUES,
  getResolutionTier,
  getScaleFactor,
  getComponentScale,
  getResolutionInfo,
  is4KOrHigher,
  isAssemblyLineResolution,
};

